import streamSaver from 'streamsaver';
import * as zip from '@zip.js/zip.js';
import { Modal } from 'antd';
import { DownloadingState, useStore } from '~src/store';
import { appLog } from '~src/utils/log';
import { buildFolderWithPathStructure } from '~src/utils/downloader/shared';
import {
  getSharedTenderVersionedFilesDownloadList,
  getTenderShareFolderFilesURLs,
  loadSharedTenderSubfolders,
} from '~src/services/api/shares';
import type { DbTenderFolder, DbFile, DbAttachment } from '~src/types/db';

// --- Types ---
type MixedFile = DbFile & DbAttachment;

interface DownloadVendorMultipleParams {
  folders: DbTenderFolder[];
  files: MixedFile[];
  allFolders: DbTenderFolder[];
  shareId: string;
  shareKey: string;
  currentFolderId?: string | number | null;
  rootFolderId?: string | number | null;
  fileVersionsContext: any;
  onDownloadStarted?: () => void;
}

// --- ZipStream Class ---
class ZipStream {
  private zipWriter: zip.ZipWriter<WritableStream<any>> | null = null;
  private writableStream: WritableStream<any> | null = null;
  public successLog: string[] = [];
  public errorLog: string[] = [];
  public fileMap: Map<string, number> = new Map();
  public isAborted = false;

  private currentFileAlreadyStreamed = 0;
  private totalFiles = 0;
  private totalSize = 0;
  private startTime = 0;
  public endTime = 0;

  constructor(totalFiles: number, totalSize: number) {
    this.totalFiles = totalFiles;
    this.totalSize = totalSize;
    useStore.getState().changeTotalFileSize(totalSize);
  }

  start(zipName: string) {
    try {
      this.startTime = performance.now();
      this.writableStream = streamSaver.createWriteStream(zipName);
      this.zipWriter = new zip.ZipWriter(this.writableStream);
    } catch (error) {
      this.isAborted = true;
    }
  }

  private getUniqueFileName(filePath: string): string {
    let uniquePath = filePath;
    let count = 1;
    const match = filePath.match(/^(.*?)(\(\d+\))?(\.[^/.]+)?$/);
    const baseName = match?.[1] || filePath;
    const extension = match?.[3] || '';

    while (this.fileMap.has(uniquePath)) {
      uniquePath = `${baseName}(${count})${extension}`;
      count++;
    }

    this.fileMap.set(uniquePath, count);
    return uniquePath;
  }

  async addFile(fullPath: string, blob: Blob) {
    if (this.isAborted || !this.zipWriter) return;

    const uniquePath = this.getUniqueFileName(fullPath);
    try {
      useStore.getState().changeDownloadingState(DownloadingState.FETCHING);
      await this.zipWriter.add(uniquePath, new zip.BlobReader(blob));
      this.currentFileAlreadyStreamed++;
      useStore.getState().currentDownloadFile(this.currentFileAlreadyStreamed);
      this.successLog.push(
        `${uniquePath} - success (${(blob.size / 1024 / 1024).toFixed(2)} MB)`
      );
    } catch (error: any) {
      const msg = `${uniquePath} (0.00 MB) - ERROR: ${error?.message}`;
      this.errorLog.push(msg);
      useStore.getState().addError(msg);
      this.isAborted = true;
    }
  }

  async addEmptyFolder(folderPath: string) {
    if (this.isAborted || !this.zipWriter) return;
    try {
      const uniqueFolderPath = folderPath.endsWith('/')
        ? folderPath
        : folderPath + '/';

      if (this.fileMap.has(uniqueFolderPath)) return;

      await this.zipWriter.add(uniqueFolderPath, new zip.TextReader(''));
      this.fileMap.set(uniqueFolderPath, 1);
    } catch (error) {
      // Ignore creation errors
    }
  }

  async finish() {
    try {
      if (!this.zipWriter || this.isAborted) return;
      if (this.endTime !== 0) return;

      this.endTime = performance.now();
      const totalSizeMB = (this.totalSize / 1024 / 1024).toFixed(2);
      const timeTaken = (this.endTime - this.startTime) / 1000;
      const speed = (this.totalSize / 1024 / 1024 / timeTaken).toFixed(2);
      const totalErrors = this.errorLog.length;

      const mergedLogs: string[] = [];
      const successEntries = [...this.successLog];
      const errorEntries = [...this.errorLog];
      while (successEntries.length || errorEntries.length) {
        if (successEntries.length) mergedLogs.push(successEntries.shift()!);
        if (errorEntries.length) mergedLogs.push(errorEntries.shift()!);
      }

      const finalLog = [
        ...mergedLogs,
        `\n==== Summary V2 ====`,
        `Total files - ${this.totalFiles}`,
        `Total errors - ${totalErrors}`,
        `Total size - ${totalSizeMB} MB`,
        `Download speed - ${speed} MB/s`,
        `Time taken - ${timeTaken.toFixed(2)} s`,
      ];
      if (totalErrors > 0) {
        finalLog.push(`\n==== ${totalErrors} Errors ====`, ...this.errorLog);
      }

      const logBlob = new Blob([finalLog.join('\n')], { type: 'text/plain' });
      if (!this.fileMap.has('download_log.txt')) {
        await this.zipWriter.add(
          'download_log.txt',
          new zip.BlobReader(logBlob)
        );
      }

      await this.zipWriter.close();
      useStore.getState().changeDownloadingState(DownloadingState.FINISHING);
      setTimeout(
        () => useStore.getState().changeDownloadingActive(false),
        1500
      );
    } catch (error) {
      this.isAborted = true;
    }
  }
}

// --- Workers ---
const createWorker = () => {
  return new Worker(
    URL.createObjectURL(
      new Blob(
        [
          `self.onmessage = async (event) => {
            const { file } = event.data;
            try {
              const response = await fetch(file.url);
              if (!response.ok) {
                throw new Error(\`HTTP \${response.status} - \${response.statusText}\`);
              }
              const blob = await response.blob();
              self.postMessage({ success: true, file, blob });
            } catch (error) {
              self.postMessage({ success: false, file, error: error.message });
            }
          };`,
        ],
        { type: 'application/javascript' }
      )
    )
  );
};

// --- Helpers ---
const uniqFoldersById = (folders: DbTenderFolder[]) => {
  const map = new Map<string, DbTenderFolder>();
  for (const f of folders) map.set(String(f.id), f);
  return Array.from(map.values());
};

// The API endpoint GET /tender_shares/{share_id}/tender_folders/{root_folder_id}/folders
// returns ALL descendant subfolders of a root folder in one flat call (parent_id allows
// local tree building). root_folder_id MUST be a true root-level folder id – calling
// it with a non-root id returns 400 from TenderRootFolderCheckMiddleware.
// Therefore: one call per root, no recursion needed.
const loadAllSharedSubfolders = async ({
  shareId,
  shareKey,
  rootFolderIds,
}: {
  shareId: string;
  shareKey: string;
  rootFolderIds: number[];
}): Promise<DbTenderFolder[]> => {
  const result: DbTenderFolder[] = [];

  for (const rootId of rootFolderIds) {
    try {
      const children = await loadSharedTenderSubfolders(
        ['shared-subfolders', shareId, rootId],
        { share_id: shareId, share_key: shareKey, root_folder_id: rootId }
      );
      if (children?.length) result.push(...children);
    } catch (e) {
      // continue to next root
    }
  }

  return uniqFoldersById(result);
};

const ModalConfirmDownload = async () => {
  return new Promise<boolean>((resolve) => {
    Modal.confirm({
      title: 'Large download',
      content:
        'Selected content is large (>500 files). Downloading may take time. Continue?',
      okText: 'Download',
      cancelText: 'Cancel',
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });
};

// ХЕЛПЕР ДЛЯ ПОШУКУ СПРАВЖНЬОГО ROOT FOLDER ID У ДЕРЕВІ
const getActualRootFolderId = (
  targetFolderId: string | number | null | undefined,
  tree: any[]
): string | number => {
  if (!targetFolderId) return '';
  let current = tree.find((f) => String(f.id) === String(targetFolderId));
  while (current && current.parent_id) {
    const parent = tree.find((f) => String(f.id) === String(current.parent_id));
    if (!parent) break;
    current = parent;
  }
  return current?.id || targetFolderId;
};

// --- Main Function ---
export const downloadVendorTenderFolderMultiple = async ({
  folders,
  files,
  allFolders,
  shareId,
  shareKey,
  currentFolderId,
  rootFolderId,
  fileVersionsContext,
  onDownloadStarted,
}: DownloadVendorMultipleParams): Promise<void> => {
  try {
    const fileQueue: any[] = [];
    let totalFiles = 0;
    let totalSize = 0;
    const folderPathsToCreate: string[] = [];

    // -------------------------------------------------------------
    // PREPARE: Global Context Tree
    // -------------------------------------------------------------
    // NOTE: We deliberately skip GET /tender_shares/{share_id}/tender_folders
    // here. That endpoint is currently gated by TenderRootFolderCheckMiddleware
    // on the server and returns 400. In the vendor-subfolder context we already
    // know the authoritative `rootFolderId` from the URL/props, so we derive
    // sharedRootFolders from local state instead.
    const localTree = uniqFoldersById([...(allFolders || []), ...folders]);

    // Walk up the parent chain in the local tree starting from a given id.
    // Returns the topmost ancestor we can reach locally (may itself be non-root
    // if the local tree is incomplete).
    const walkUpToLocalTop = (
      id: string | number
    ): DbTenderFolder | undefined => {
      let current = localTree.find((f) => String(f.id) === String(id));
      while (current && current.parent_id != null) {
        const parent = localTree.find(
          (f) => String(f.id) === String(current!.parent_id)
        );
        if (!parent) break;
        current = parent;
      }
      return current;
    };

    let sharedRootFolders: DbTenderFolder[] = [];
    if (rootFolderId) {
      const top = walkUpToLocalTop(rootFolderId);
      if (top) sharedRootFolders = [top];
    }
    if (!sharedRootFolders.length) {
      const idSet = new Set(localTree.map((f) => String(f.id)));
      sharedRootFolders = localTree.filter(
        (f) => !f.parent_id || !idSet.has(String(f.parent_id))
      );
    }

    // The API's TenderRootFolderCheckMiddleware rejects any id whose
    // parent_id is not null. We only call it for ids we can prove are roots;
    // for the rest we fall back to the local tree as the descendants source.
    const provableRootIds = sharedRootFolders
      .filter((f) => f.parent_id == null)
      .map((f) => Number(f.id));
    const allDescendants = provableRootIds.length
      ? await loadAllSharedSubfolders({
          shareId,
          shareKey,
          rootFolderIds: provableRootIds,
        })
      : allFolders || [];

    const fullContextTree = uniqFoldersById([
      ...(allFolders || []),
      ...(sharedRootFolders || []),
      ...(allDescendants || []),
      ...folders,
    ]);

    // -------------------------------------------------------------
    // 1. Process Selected Folders
    // -------------------------------------------------------------
    for (const folder of folders) {
      const rootFolderName = folder.name;
      const structure = buildFolderWithPathStructure(folder, fullContextTree);

      const rootFolderEntry = structure.find(
        (s: any) => String(s.id) === String(folder.id)
      );

      let parentPathPrefix = '';
      if (rootFolderEntry && rootFolderEntry.path) {
        const parts = rootFolderEntry.path.split('/');
        if (parts.length > 1) {
          parts.pop();
          parentPathPrefix = parts.join('/') + '/';
        }
      }

      const currentFolderPaths = uniqFoldersById(
        structure.map((s: any) => ({ id: s.id, name: s.path } as any))
      )
        .map((x: any) => x.name)
        .filter(Boolean)
        .map((p: string) => {
          if (parentPathPrefix && p.startsWith(parentPathPrefix)) {
            return p.substring(parentPathPrefix.length);
          }
          return p;
        });

      const adjustedRootName = rootFolderName;
      const allPathsSet = new Set<string>(currentFolderPaths);
      if (!allPathsSet.has(adjustedRootName)) {
        currentFolderPaths.unshift(adjustedRootName);
      }
      folderPathsToCreate.push(...currentFolderPaths);

      // ВИРАХОВУЄМО СПРАВЖНІЙ ROOT ID ЗАМІСТЬ folder.parent_id (ЛИШЕ ДЛЯ ПАПОК)
      const resolvedRootFolderId =
        rootFolderId || getActualRootFolderId(folder.id, fullContextTree);

      // Fetch Files inside Folder using the new versioned API endpoint
      const folderFiles = await getSharedTenderVersionedFilesDownloadList({
        shareId: String(shareId),
        shareKey,
        rootFolderId: String(resolvedRootFolderId),
        folderId: String(folder.id),
        recursive: true,
      });

      if (folderFiles && folderFiles.length > 0) {
        totalFiles += folderFiles.length;
        totalSize += folderFiles.reduce(
          (acc: number, f: any) => acc + (f.file_size || 0),
          0
        );

        folderFiles.forEach((file: any) => {
          const fileFolderId = file.folder_id;

          const rawFolderPath =
            structure.find((f: any) => {
              if (fileFolderId == null) return false;
              return (
                String(f.id) === String(fileFolderId) ||
                (f.using_key != null &&
                  String(f.using_key) === String(fileFolderId))
              );
            })?.path || '';

          let safeFolderPath = rawFolderPath;
          if (parentPathPrefix && rawFolderPath.startsWith(parentPathPrefix)) {
            safeFolderPath = rawFolderPath.substring(parentPathPrefix.length);
          }

          if (!safeFolderPath) safeFolderPath = rootFolderName;

          fileQueue.push({
            using_key: file.using_key,
            fullPath: safeFolderPath
              ? `${safeFolderPath}/${file.file_name}`
              : file.file_name,
          });
        });
      }
    }

    // -------------------------------------------------------------
    // 2. Process Selected Individual Files
    // -------------------------------------------------------------
    for (const file of files) {
      const fileName =
        file.name || file.title || (file as any).file_name || `file-${file.id}`;

      let calculatedPath = fileName;
      const fileFolderId = file.folder_id;

      if (fileFolderId) {
        const parentFolder = fullContextTree.find(
          (f) => String(f.id) === String(fileFolderId)
        );

        if (parentFolder) {
          const struct = buildFolderWithPathStructure(
            parentFolder,
            fullContextTree
          );
          const folderEntry = struct.find(
            (s) => String(s.id) === String(fileFolderId)
          );

          if (folderEntry && folderEntry.path) {
            let fullPath = folderEntry.path;

            for (const root of sharedRootFolders) {
              const rootPrefix = root.name + '/';
              if (fullPath.startsWith(rootPrefix)) {
                fullPath = fullPath.substring(rootPrefix.length);
                break;
              } else if (fullPath === root.name) {
                fullPath = '';
                break;
              }
            }

            calculatedPath = fullPath ? `${fullPath}/${fileName}` : fileName;
            if (fullPath) folderPathsToCreate.push(fullPath);
          }
        }
      }

      const versionId = fileVersionsContext[file.id]?.id;
      const stableVersions = (file.files || [])
        .map((f: any) => ({
          ...f,
          revision: f.serial_number ?? f.version ?? f.revision,
          version: f.serial_number ?? f.version ?? f.revision,
          id: f.registration_instance_id || f.file_id || f.id,
        }))
        .sort((a: any, b: any) => (b.revision || 0) - (a.revision || 0));

      const selectedVersion =
        stableVersions.find((v: any) => String(v.id) === String(versionId)) ||
        stableVersions[0] ||
        file.versions?.find((v: any) => String(v.id) === String(versionId)) ||
        file.versions?.[0];

      if (!selectedVersion) continue;

      const regId =
        selectedVersion.registration_id ||
        selectedVersion.versioned_file_id ||
        file.id;
      const serial = selectedVersion.version || selectedVersion.serial_number;

      // STATIC TENDER ROOT FOLDER - REQUIRES API
      const downloadList = await getSharedTenderVersionedFilesDownloadList({
        shareId: String(shareId),
        shareKey,
        rootFolderId: String(rootFolderId || file.folder_id),
        folderId: String(currentFolderId || file.folder_id),
        recursive: false,
        registration_ids: String(regId),
        serial,
      });

      if (downloadList && downloadList.length > 0) {
        totalFiles += 1;
        totalSize += downloadList[0].file_size || 0;

        fileQueue.push({
          using_key: downloadList[0].using_key,
          fullPath: calculatedPath,
        });
      }
    }

    // -------------------------------------------------------------
    // 3. Start Download
    // -------------------------------------------------------------
    if (fileQueue.length === 0 && folderPathsToCreate.length === 0) {
      throw new Error('No files or folders to download');
    }

    if (totalFiles > 500) {
      const confirm = await ModalConfirmDownload();
      if (!confirm) return;
    }

    if (onDownloadStarted) onDownloadStarted();

    const zipStream = new ZipStream(totalFiles, totalSize);
    useStore.getState().totalDownloadFiles(totalFiles);
    zipStream.start(`C-WEB-Share-${Date.now()}.zip`);

    if (zipStream.isAborted) return;

    const uniquePaths = [...new Set(folderPathsToCreate)];
    for (const p of uniquePaths) {
      await zipStream.addEmptyFolder(p);
    }

    if (fileQueue.length === 0) {
      await zipStream.finish();
      return;
    }

    // -------------------------------------------------------------
    // 4. Worker Processing
    // -------------------------------------------------------------
    const NUM_WORKERS = 5;
    const workers: Worker[] = [];
    let activeWorkers = 0;

    for (let i = 0; i < NUM_WORKERS; i++) {
      workers.push(createWorker());
    }

    const finishIfDone = async () => {
      if (fileQueue.length === 0 && activeWorkers === 0) {
        setTimeout(async () => {
          if (fileQueue.length === 0 && activeWorkers === 0) {
            await zipStream.finish();
            workers.forEach((w) => w.terminate());
          }
        }, 300);
      }
    };

    const assignFileToWorker = async (worker: Worker) => {
      if (zipStream.isAborted) return;

      const nextItem = fileQueue.shift();
      if (!nextItem) {
        await finishIfDone();
        return;
      }
      activeWorkers++;

      try {
        // Pass rootFolderId so TenderRootFolderCheckMiddleware is satisfied.
        // VFDownloadUrlUsingKeyVM has { key, url } – fall back to [0].url.
        const urls = await getTenderShareFolderFilesURLs(
          String(shareId),
          shareKey,
          [nextItem.using_key],
          rootFolderId ?? undefined
        );

        const url =
          (urls || []).find(
            (u: any) => String(u?.key) === String(nextItem.using_key)
          )?.url || urls?.[0]?.url;

        if (!url) {
          throw new Error(`Missing URL for ${nextItem.fullPath}`);
        }

        worker.postMessage({
          file: { url, file_name: nextItem.fullPath },
        });
      } catch (err: any) {
        const errMsg = `${nextItem.fullPath} - ERROR: ${err.message}`;
        zipStream.errorLog.push(errMsg);
        useStore.getState().addError(errMsg);
        activeWorkers--;

        if (fileQueue.length > 0) {
          await assignFileToWorker(worker);
        } else {
          await finishIfDone();
        }
      }
    };

    for (const worker of workers) {
      worker.onmessage = async (event) => {
        const { success, file, blob, error } = event.data;
        try {
          if (success) {
            await zipStream.addFile(file.file_name, blob);
          } else {
            const msg = `${file.file_name} - ERROR: ${error}`;
            zipStream.errorLog.push(msg);
            useStore.getState().addError(msg);
          }
        } finally {
          activeWorkers--;
          if (fileQueue.length > 0) {
            await assignFileToWorker(worker);
          } else {
            await finishIfDone();
          }
        }
      };

      worker.onerror = async (e) => {
        const msg = `Worker Error: ${e.message}`;
        zipStream.errorLog.push(msg);
        useStore.getState().addError(msg);
        activeWorkers--;
        await finishIfDone();
      };

      await assignFileToWorker(worker);
    }
  } catch (err) {
    appLog('downloadVendorTenderFolderMultiple -> global error', err);
    useStore.getState().changeDownloadingActive(false);
  }
};
